"use client";

import { useState, useEffect } from "react";
import { getTranslation } from "@/i18n/get-translation";
import { getConsentStatus, updateConsent } from "@/utils/analytics";

interface ICookieConsent {
  locale: string;
}

export default function CookieConsent({ locale }: ICookieConsent) {
  const t = getTranslation(locale, "cookieConsent");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const status = getConsentStatus();
    if (status === null) {
      setVisible(true);
    }

    function handleReset() {
      setVisible(true);
    }
    window.addEventListener("cookie-consent-reset", handleReset);
    return () => window.removeEventListener("cookie-consent-reset", handleReset);
  }, []);

  function handleAccept() {
    updateConsent("granted");
    setVisible(false);
  }

  function handleDecline() {
    updateConsent("denied");
    setVisible(false);
  }

  if (!visible) return null;

  const privacyUrl = `/${locale}/privacy-policy`;

  return (
    <div
      role="dialog"
      aria-label={t("title")}
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-6 py-4"
    >
      <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-4">
        <p className="m-0 flex-[1_1_400px] text-sm leading-relaxed text-primary-800">
          {t("message")}{" "}
          <a href={privacyUrl} className="text-primary-700 underline">
            {t("privacyLink")}
          </a>
        </p>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={handleDecline}
            className="px-5 py-2 text-sm font-medium border border-primary-800 rounded-lg bg-transparent text-primary-800 cursor-pointer hover:bg-primary-800/5"
          >
            {t("decline")}
          </button>
          <button
            onClick={handleAccept}
            className="px-5 py-2 text-sm font-medium border-none rounded-lg bg-primary-800 text-white cursor-pointer hover:bg-primary-600"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
